import { ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "src/lib/utils";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "accept" | "decline" | "outline" | "toggle";
  fullWidth?: boolean;
  size?: "sm" | "md" | "lg";
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      fullWidth = false,
      size = "md",
      className,
      children,
      type = "button",
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center rounded-md font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-100/50 focus:ring-offset-2 disabled:opacity-50 transition-colors duration-200 border";

    const variants = {
      primary:
        "bg-purple-100 text-white hover:bg-dark-purple-100 border-purple-100 hover:border-dark-purple-100",
      secondary:
        "bg-white dark:bg-gray-800 text-dark-100 dark:text-gray-200 ring-1 ring-inset ring-dark-20 dark:ring-gray-600 hover:bg-dark-5 dark:hover:bg-gray-700",
      accept: "bg-green text-white hover:bg-green/90 border-green hover:border-green/90",
      decline: "bg-red text-white hover:bg-red/90 border-red hover:border-red/90",
      outline:
        "bg-white dark:bg-gray-800 text-dark-60 dark:text-gray-400 ring-1 ring-inset ring-dark-20 dark:ring-gray-600 hover:bg-dark-5 dark:hover:bg-gray-700 hover:text-dark-100 dark:hover:text-gray-200",
      toggle:
        "border-violet-400 bg-violet-200 text-purple-100 hover:bg-violet-300 hover:border-violet-400",
    };

    const sizes = {
      sm: "px-2 py-1 text-sm",
      md: "px-3 py-2 text-sm",
      lg: "px-4 py-2 text-base",
    };

    const widths = {
      true: "w-full",
      false: variant === "accept" || variant === "decline" ? "w-16" : "w-auto",
    };

    return (
      <button
        ref={ref}
        type={type}
        aria-label={ariaLabel}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          widths[String(fullWidth) as "true" | "false"],
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
